import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Drawer,
  Group,
  Stack,
  Switch,
  TextInput,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { useCreateSupplier, useUpdateSupplier } from './purchasingApi';
import type { SupplierDto } from './types';

interface Props {
  supplier: SupplierDto | null;
  opened: boolean;
  onClose: () => void;
}

// Drawer form for create + edit — same pattern as ItemFormDrawer.
export function SupplierFormDrawer({ supplier, opened, onClose }: Props) {
  const { t } = useTranslation();
  const create = useCreateSupplier();
  const update = useUpdateSupplier();
  const editing = supplier !== null;

  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [address, setAddress] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setNameAr(supplier?.nameAr ?? '');
      setNameEn(supplier?.nameEn ?? '');
      setContactName(supplier?.contactName ?? '');
      setContactPhone(supplier?.contactPhone ?? '');
      setContactEmail(supplier?.contactEmail ?? '');
      setAddress(supplier?.address ?? '');
      setTaxNumber(supplier?.taxNumber ?? '');
      setNotes(supplier?.notes ?? '');
      setIsActive(supplier?.isActive ?? true);
      setError(null);
    }
  }, [opened, supplier]);

  const submit = async () => {
    setError(null);
    const payload = {
      nameAr: nameAr.trim(),
      nameEn: nameEn.trim() || null,
      contactName: contactName.trim() || null,
      contactPhone: contactPhone.trim() || null,
      contactEmail: contactEmail.trim() || null,
      address: address.trim() || null,
      taxNumber: taxNumber.trim() || null,
      notes: notes.trim() || null,
    };

    try {
      if (editing) {
        await update.mutateAsync({ id: supplier!.supplierId, req: { ...payload, isActive } });
      } else {
        await create.mutateAsync(payload);
      }
      notifications.show({ color: 'green', title: t('purchasing.supplier.saved'), message: payload.nameAr });
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail ?? (err instanceof Error ? err.message : t('purchasing.supplier.saveFailed')));
    }
  };

  const valid = nameAr.trim().length > 0;
  const pending = create.isPending || update.isPending;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title={editing ? t('purchasing.supplier.edit') : t('purchasing.supplier.create')}
    >
      <Stack gap="md">
        <TextInput
          label={t('purchasing.supplier.nameAr')}
          value={nameAr}
          onChange={(e) => setNameAr(e.currentTarget.value)}
          required
        />
        <TextInput
          label={t('purchasing.supplier.nameEn')}
          value={nameEn}
          onChange={(e) => setNameEn(e.currentTarget.value)}
          dir="ltr"
        />
        <Group grow>
          <TextInput
            label={t('purchasing.supplier.contactName')}
            value={contactName}
            onChange={(e) => setContactName(e.currentTarget.value)}
          />
          <TextInput
            label={t('purchasing.supplier.contactPhone')}
            value={contactPhone}
            onChange={(e) => setContactPhone(e.currentTarget.value)}
            dir="ltr"
          />
        </Group>
        <TextInput
          label={t('purchasing.supplier.contactEmail')}
          value={contactEmail}
          onChange={(e) => setContactEmail(e.currentTarget.value)}
          dir="ltr"
        />
        <Textarea
          label={t('purchasing.supplier.address')}
          value={address}
          onChange={(e) => setAddress(e.currentTarget.value)}
          minRows={2}
        />
        <TextInput
          label={t('purchasing.supplier.taxNumber')}
          value={taxNumber}
          onChange={(e) => setTaxNumber(e.currentTarget.value)}
          dir="ltr"
        />
        <Textarea
          label={t('purchasing.supplier.notes')}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={2}
        />
        {editing && (
          <Switch
            label={t('purchasing.supplier.active')}
            checked={isActive}
            onChange={(e) => setIsActive(e.currentTarget.checked)}
          />
        )}

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} stroke={1.6} />}>{error}</Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button color="wheels" onClick={submit} loading={pending} disabled={!valid}>
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
